import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useGetDepartmentsQuery } from '../../Redux/Service/Department';
import { useGetRolesQuery } from '../../Redux/Service/RoleApi';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useUpdateUserMutation, useGetUserByIdQuery } from '../../Redux/Service/UserApi';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StyledDashboardLayout from '../style';
import { Grid, FormHelperText } from '@mui/material';
import { useState, useEffect } from 'react';
import { API_URL } from '../../Config';

function UserEdit() {
   const { id } = useParams();
   const navigate = useNavigate();
   const [signaturePreviews, setSignaturePreviews] = useState({});
   const [fileError, setFileError] = useState(null);
   const [removeExistingSignature, setRemoveExistingSignature] = useState(false);
   const [updateUser] = useUpdateUserMutation();
   const {
      data: userData,
      isLoading: isLoadingForUser,
      error: errorForUser,
      refetch,
   } = useGetUserByIdQuery(id);
   const {
      data: departmentsData,
      isLoading: isLoadingForDepartment,
      error: errorForDepartment,
   } = useGetDepartmentsQuery();
   const { data: rolesData, isLoading: isLoadingForRole, error: errorForRole } = useGetRolesQuery();
   const emailRegex = /^(?!.*\.\.)(?!.*@@)[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;

   const validationSchema = Yup.object({
      name: Yup.string()
         .matches(/^[a-zA-Z\s]+$/, 'Name can only contain letters and spaces')
         .matches(/^[a-zA-Z]/, 'Name cannot start with a space')
         .max(50, 'Name should be atmost 50 characters')
         .min(2, 'Name must be at least 2 characters')
         .required('Name is required'),
      email: Yup.string()
         .test('valid-email', 'Please enter a valid email address', (value) => {
            if (!value) return true;

            // Additional checks
            if (value.startsWith('.')) return false;
            if (value.endsWith('.')) return false;
            if (value.includes('..')) return false;

            return emailRegex.test(value);
         })
         .max(254, 'Email should be atmost 254 characters')
         .required('Email is required'),
      phoneNo: Yup.string()
         .matches(/^$|^[0-9]+$/, 'Phone number can only contain digits')
         .min(10, 'Phone number must be at least 10 digits')
         .max(15, 'Phone number cannot exceed 15 digits')
         .notRequired(),
      roleId: Yup.string().required('Role is required'),
      departmentId: Yup.string().required('Department is required'),
      signature: Yup.mixed().nullable().notRequired(),
   });

   const handleNameInput = (e, setFieldValue) => {
      const value = e.target.value;

      if (value.length === 1 && value === ' ') {
         return;
      }

      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
      const cleanedValue = filteredValue.replace(/\s+/g, ' ');

      setFieldValue('name', cleanedValue);
   };

   const handlePhoneInput = (e, setFieldValue) => {
      const value = e.target.value;
      const filteredValue = value.replace(/[^0-9]/g, '');
      setFieldValue('phoneNo', filteredValue);
   };

   const handleSignatureUpload = (event, setFieldValue) => {
      const file = event.currentTarget.files[0];

      if (!file) return;

      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const maxFileSize = 2 * 1024 * 1024; // 2MB

      if (!validImageTypes.includes(file.type)) {
         toast.error('Only JPEG, JPG, and PNG files are allowed for signature');
         setFileError('Only JPEG, JPG, and PNG files are allowed for signature');
         event.currentTarget.value = '';
         return;
      }

      if (file.size > maxFileSize) {
         toast.error('Signature file size must not exceed 2MB');
         setFileError('File too big, should be within 2MB');
         event.currentTarget.value = '';
         return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
         setSignaturePreviews((prev) => ({
            ...prev,
            signature: e.target.result,
         }));
      };
      reader.readAsDataURL(file);
      setFieldValue('signature', file);
      setFileError('');
   };

   const handleRemoveSignature = (setFieldValue, isExisting = false) => {
      if (isExisting) {
         setRemoveExistingSignature(true);
      } else {
         setSignaturePreviews((prev) => ({
            ...prev,
            signature: null,
         }));
         setFieldValue('signature', null);
         const signatureInput = document.getElementById('signature');
         if (signatureInput) {
            signatureInput.value = '';
         }
      }
   };

   const handleSubmit = async (values, { resetForm, setSubmitting }) => {
      try {
         setSubmitting(true);
         const formData = new FormData();
         formData.append('id', parseInt(id));
         formData.append('name', values.name.trim());
         formData.append('email', values.email.trim().toLowerCase());
         formData.append('phoneNo', values.phoneNo.trim());
         formData.append('roleId', parseInt(values.roleId));
         formData.append('departmentId', parseInt(values.departmentId));

         if (removeExistingSignature) {
            formData.append('removeSignature', 'true');
         } else if (values.signature && values.signature instanceof File) {
            formData.append('signature', values.signature);
         }

         const updateUserResponse = await updateUser(formData).unwrap();

         if (updateUserResponse) {
            toast.success(updateUserResponse?.message || 'User updated successfully');

            await refetch();
            setRemoveExistingSignature(false);

            setTimeout(() => {
               navigate('/user');
            }, 100);
         } else {
            toast.error('Failed to update user');
         }
      } catch (error) {
         console.error('Error updating user:', error);

         if (error?.data?.message) {
            toast.error(error.data.message);
         } else if (error?.message) {
            toast.error(error.message);
         } else if (error?.status === 400) {
            toast.error('Invalid data provided. Please check your inputs.');
         } else if (error?.status === 404) {
            toast.error('User not found');
         } else if (error?.status === 500) {
            toast.error('Server error. Please try again later.');
         } else {
            toast.error('Something went wrong. Please try again later!');
         }
      } finally {
         setSubmitting(false);
      }
   };

   if (isLoadingForRole || isLoadingForDepartment || isLoadingForUser) {
      return (
         <StyledDashboardLayout>
            <div className="content-wrapper">
               <div className="content-header">
                  <div className="container-fluid">
                     <div className="d-flex justify-content-between">
                        <div className="pageTitle">
                           <h1 className="m-0">Edit User</h1>
                        </div>
                        <div className="btnWrap">
                           <Link to="/user" className="btn addBtn">
                              <ArrowBackIcon /> Back
                           </Link>
                        </div>
                     </div>
                  </div>
               </div>
               <div className="card customCard p-3 m-3">
                  <div className="card-body text-center">
                     <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                     </div>
                     <p className="mt-2">Loading user data...</p>
                  </div>
               </div>
            </div>
         </StyledDashboardLayout>
      );
   }

   if (errorForRole || errorForDepartment || errorForUser) {
      return (
         <StyledDashboardLayout>
            <div className="content-wrapper">
               <div className="content-header">
                  <div className="container-fluid">
                     <div className="d-flex justify-content-between">
                        <div className="pageTitle">
                           <h1 className="m-0">Edit User</h1>
                        </div>
                        <div className="btnWrap">
                           <Link to="/user" className="btn addBtn">
                              <ArrowBackIcon /> Back
                           </Link>
                        </div>
                     </div>
                  </div>
               </div>
               <div className="card customCard p-3 m-3">
                  <div className="card-body">
                     <div className="alert alert-danger">
                        Error loading data:{' '}
                        {errorForRole?.message ||
                           errorForDepartment?.message ||
                           errorForUser?.message}
                        <button
                           className="btn btn-sm btn-outline-danger ml-2"
                           onClick={() => window.location.reload()}
                        >
                           Retry
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         </StyledDashboardLayout>
      );
   }

   const existingSignature = userData?.response?.signature;

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between">
                     <div className="pageTitle">
                        <h1 className="m-0">Edit User</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/user" className="btn addBtn">
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>
            <div className="card customCard p-3 m-3">
               <div className="card-header">
                  <h3 className="card-title">Edit User</h3>
               </div>
               <div className="card-body">
                  <Formik
                     initialValues={{
                        name: userData?.response?.name || '',
                        email: userData?.response?.email || '',
                        phoneNo: userData?.response?.phoneNo || '',
                        roleId: userData?.response?.role?.id?.toString() || '',
                        departmentId: userData?.response?.department?.id?.toString() || '',
                        signature: '',
                     }}
                     validationSchema={validationSchema}
                     onSubmit={handleSubmit}
                     enableReinitialize={true}
                  >
                     {({ setFieldValue, values, touched, errors, isSubmitting }) => (
                        <Form>
                           <div className="form-group">
                              <label htmlFor="name">Name *</label>
                              <Field
                                 type="text"
                                 name="name"
                                 className="form-control"
                                 value={values.name}
                                 onChange={(e) => handleNameInput(e, setFieldValue)}
                                 placeholder="Enter full name (letters only, cannot start with space)"
                                 maxLength={50}
                                 disabled={isSubmitting}
                                 onError={touched.name && errors.name}
                              />
                              {/* <ErrorMessage name="name" component="div" className="text-danger" /> */}
                              <small className="form-text text-muted">
                                 Only letters and spaces are allowed. Cannot start with a space (Max
                                 50 characters & Min 2 characters).
                              </small>
                              {touched.name && (
                                 <FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                    {touched.name && errors.name}{' '}
                                 </FormHelperText>
                              )}
                           </div>
                           <Grid container spacing={4}>
                              <Grid item md={6} xs={12}>
                                 <div className="form-group">
                                    <label htmlFor="email">Email Address *</label>
                                    <Field
                                       type="email"
                                       name="email"
                                       className="form-control"
                                       placeholder="Enter valid email address"
                                       disabled={isSubmitting}
                                       onError={touched.email && errors.email}
                                    />
                                    {/* <ErrorMessage
                                       name="email"
                                       component="div"
                                       className="text-danger"
                                    /> */}
                                    {touched.email && (
                                       <FormHelperText
                                          sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                       >
                                          {touched.email && errors.email}{' '}
                                       </FormHelperText>
                                    )}
                                 </div>
                              </Grid>
                              <Grid item md={6} xs={12}>
                                 <div className="form-group">
                                    <label htmlFor="phoneNo">Phone Number</label>
                                    <Field
                                       type="text"
                                       name="phoneNo"
                                       className="form-control"
                                       value={values.phoneNo}
                                       onChange={(e) => handlePhoneInput(e, setFieldValue)}
                                       placeholder="Enter phone number (digits only, optional)"
                                       maxLength={15}
                                       disabled={isSubmitting}
                                    />
                                    {/* <ErrorMessage
                                       name="phoneNo"
                                       component="div"
                                       className="text-danger"
                                    /> */}
                                    <small className="form-text text-muted">
                                       Only numbers are allowed (10-15 digits, optional)
                                    </small>
                                 </div>
                              </Grid>
                              <Grid item md={6} xs={12}>
                                 <div className="form-group">
                                    <label htmlFor="roleId">Role *</label>
                                    <Field
                                       as="select"
                                       name="roleId"
                                       className="form-control"
                                       disabled={isSubmitting}
                                       onError={touched.roleId && errors.roleId}
                                    >
                                       <option value="">Select Role</option>
                                       {rolesData?.response?.map((role) => (
                                          <option key={role.id} value={role.id.toString()}>
                                             {role.name}
                                          </option>
                                       ))}
                                    </Field>
                                    {/* <ErrorMessage
                                       name="roleId"
                                       component="div"
                                       className="text-danger"
                                    /> */}
                                    {touched.roleId && (
                                       <FormHelperText
                                          sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                       >
                                          {touched.roleId && errors.roleId}{' '}
                                       </FormHelperText>
                                    )}
                                 </div>
                              </Grid>
                              <Grid item md={6} xs={12}>
                                 <div className="form-group">
                                    <label htmlFor="departmentId">Department *</label>
                                    <Field
                                       as="select"
                                       name="departmentId"
                                       className="form-control"
                                       disabled={isSubmitting}
                                       onError={touched.departmentId && errors.departmentId}
                                    >
                                       <option value="">Select Department</option>
                                       {departmentsData?.response?.map((department) => (
                                          <option
                                             key={department.id}
                                             value={department.id.toString()}
                                          >
                                             {department.name}
                                          </option>
                                       ))}
                                    </Field>
                                    {/* <ErrorMessage
                                       name="departmentId"
                                       component="div"
                                       className="text-danger"
                                    /> */}
                                    {touched.departmentId && (
                                       <FormHelperText
                                          sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                       >
                                          {touched.departmentId && errors.departmentId}{' '}
                                       </FormHelperText>
                                    )}
                                 </div>
                              </Grid>
                           </Grid>

                           <div className="form-group">
                              <label htmlFor="signature">Signature (Optional)</label>
                              {(signaturePreviews.signature || existingSignature) &&
                                 !signaturePreviews.signature &&
                                 !removeExistingSignature && (
                                    <div className="mb-3">
                                       <label className="d-block mb-2">Current Signature:</label>
                                       <img
                                          src={`http://localhost:7000${existingSignature}`}
                                          alt="Current Signature"
                                          style={{
                                             maxHeight: '150px',
                                             maxWidth: '300px',
                                             border: '1px solid #ddd',
                                             padding: '5px',
                                             borderRadius: '4px',
                                          }}
                                       />
                                       <div className="mt-2">
                                          <button
                                             type="button"
                                             className="btn btn-sm btn-danger"
                                             onClick={() =>
                                                handleRemoveSignature(setFieldValue, true)
                                             }
                                          >
                                             Remove Current Signature
                                          </button>
                                       </div>
                                    </div>
                                 )}
                              <div className="mb-3">
                                 {!values.signature ? (
                                    signaturePreviews.signature && (
                                       <div className="mb-3">
                                          <label className="d-block mb-2">
                                             New Signature Preview:
                                          </label>
                                          <img
                                             src={signaturePreviews.signature}
                                             alt="Signature Preview"
                                             style={{
                                                maxHeight: '150px',
                                                maxWidth: '300px',
                                                border: '1px solid #ddd',
                                                padding: '5px',
                                                borderRadius: '4px',
                                             }}
                                          />
                                          <div className="mt-2">
                                             <button
                                                type="button"
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleRemoveSignature(setFieldValue)}
                                             >
                                                Remove New Signature
                                             </button>
                                          </div>
                                       </div>
                                    )
                                 ) : (
                                    <div className="mb-3">
                                       <label className="d-block mb-2">
                                          New Signature Preview:
                                       </label>
                                       <img
                                          src={URL.createObjectURL(values.signature)}
                                          alt="Signature Preview"
                                          style={{
                                             maxHeight: '150px',
                                             maxWidth: '300px',
                                             border: '1px solid #ddd',
                                             padding: '5px',
                                             borderRadius: '4px',
                                          }}
                                       />
                                       <div className="mt-2">
                                          <button
                                             type="button"
                                             className="btn btn-sm btn-danger"
                                             onClick={() => handleRemoveSignature(setFieldValue)}
                                          >
                                             Remove New Signature
                                          </button>
                                       </div>
                                    </div>
                                 )}
                              </div>
                              <input
                                 id="signature"
                                 type="file"
                                 name="signature"
                                 accept="image/jpeg,image/jpg,image/png"
                                 onChange={(e) => handleSignatureUpload(e, setFieldValue)}
                                 className="form-control"
                                 disabled={isSubmitting}
                              />
                              <small className="form-text text-muted">
                                 Allowed formats: JPEG, JPG, PNG. Maximum file size: 2MB
                              </small>
                              {fileError && (
                                 <FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                    {fileError}{' '}
                                 </FormHelperText>
                              )}
                           </div>

                           <div className="form-group mt-3">
                              <button
                                 type="submit"
                                 className="btn commonBtn"
                                 disabled={isSubmitting}
                              >
                                 {isSubmitting ? (
                                    <>
                                       <span
                                          className="spinner-border spinner-border-sm mr-2"
                                          role="status"
                                          aria-hidden="true"
                                       ></span>
                                       Updating...
                                    </>
                                 ) : (
                                    'Update User'
                                 )}
                              </button>
                              <Link
                                 to="/user"
                                 className="btn btn-secondary ml-2"
                                 style={{ textDecoration: 'none' }}
                              >
                                 Cancel
                              </Link>
                           </div>
                        </Form>
                     )}
                  </Formik>
               </div>
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default UserEdit;
